import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, Card } from "@/components/common";
import { useEvent, useEvents } from "@/context/AppContext";
import { api } from "@/lib/api";
import { num } from "@/lib/format";
import { useHidden, HideBtn, HiddenRow } from "@/components/HiddenProfiles";

const tone = (n) => (n === 0 ? { text: "text-emerald-400", bar: "bg-emerald-500" } : n < 10 ? { text: "text-yellow-300", bar: "bg-yellow-400" }
  : n < 30 ? { text: "text-orange-400", bar: "bg-orange-500" } : { text: "text-red-400", bar: "bg-red-500" });
const RELOAD = ["MESSAGES_ACCOUNT_LIST_UPDATED", "rt.resync"];
const byUnread = (a, b) => b.unread - a.unread || (a.username || "").toLowerCase().localeCompare((b.username || "").toLowerCase());

function ProfileCard({ p, max, onHide }) {
  const t = tone(p.unread);
  const on = p.status === "connected";
  return (
    <Link to={`/messages?a=${p.id}&f=unread`} className="relative rounded-lg border border-[#232738] bg-[#0D0E14] p-3 transition-colors hover:border-violet-500/50" data-testid={`unread-profile-${p.username}`}>
      {onHide && <HideBtn onClick={onHide} testid={`unread-profile-hide-${p.username}`} />}
      <div className="flex items-center gap-2.5">
        <div className="relative shrink-0">
          <Avatar name={p.creator_name || p.username} url={p.avatar_url} size={36} />
          <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[#0D0E14] ${on ? "bg-emerald-400" : "bg-red-500"}`} title={on ? "Telegram connesso" : "Telegram scollegato"} data-testid={`unread-profile-status-${p.username}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{p.creator_name || "—"}</div>
          <div className="truncate text-xs text-slate-500">@{p.username}</div>
        </div>
        <div className={`num text-2xl font-extrabold ${t.text}`} data-testid={`unread-profile-count-${p.username}`}>{num(p.unread)}</div>
      </div>
      <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded bg-white/5">
        <div className={`h-full rounded ${t.bar} transition-[width] duration-500`} style={{ width: `${max ? Math.max(p.unread ? 4 : 0, (p.unread / max) * 100) : 0}%` }} />
      </div>
    </Link>
  );
}

// CHAT NON LETTE PER PROFILO: same numbers as the profile badges in Messages, live from the server counters (UNREAD_COUNTERS).
// Loaded once; re-read only when the profile list changes or after a realtime reconnect.
export function UnreadByProfile({ className = "mb-3", onTotal }) {
  const [d, setD] = useState(null);
  const t = useRef(null);
  const load = useCallback(() => api.get("/dashboard/unread-profiles", { timeout: 9000 }).then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); return () => clearTimeout(t.current); }, [load]);
  useEvents(RELOAD, () => { clearTimeout(t.current); t.current = setTimeout(load, 600); });
  useEvent("UNREAD_COUNTERS", (c) => setD((p) => {
    if (!p || !p.items.some((x) => x.id === c.account_id)) return p;
    const items = p.items.map((x) => (x.id === c.account_id ? { ...x, unread: c.unread } : x)).sort(byUnread);
    return { items, total: items.reduce((s, x) => s + x.unread, 0) };
  }));
  useEvent("account.status", (c) => setD((p) => p && { ...p, items: p.items.map((x) => (x.id === c.account_id ? { ...x, status: c.status } : x)) }));
  const hd = useHidden("unread");
  useEffect(() => { if (d && onTotal) onTotal(d.items.filter((x) => !hd.hidden.includes(x.id)).reduce((s, x) => s + x.unread, 0)); }, [d, hd.hidden]); // eslint-disable-line
  if (!d) return null;
  const vis = d.items.filter((x) => !hd.hidden.includes(x.id));
  const hid = d.items.filter((x) => hd.hidden.includes(x.id));
  const tot = vis.reduce((s, x) => s + x.unread, 0);
  const max = Math.max(0, ...vis.map((x) => x.unread));
  return (
    <Card title="Chat non lette per profilo" className={className} testid="unread-by-profile"
      actions={<span className="text-xs text-slate-400" data-testid="unread-by-profile-total">Totale <span className="num font-bold text-white">{num(tot)}</span>{hid.length > 0 && <> · {num(hid.reduce((s, x) => s + x.unread, 0))} nei nascosti</>}</span>}>
      {d.items.length ? (<>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">{vis.map((p) => <ProfileCard key={p.id} p={p} max={max} onHide={hd.canEdit ? () => hd.set(p.id, true) : null} />)}</div>
        <HiddenRow items={hid} value={(p) => p.unread} canEdit={hd.canEdit} onShow={(id) => hd.set(id, false)} testid="unread-hidden" /></>
      ) : <div className="py-4 text-center text-xs text-slate-500">Nessun profilo collegato</div>}
    </Card>
  );
}
